import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Adresse, ContratLocationCreation, EtatContratLocation } from '@artosera/shared';
import { useMutation } from './mutation.js';
import { api, requeteMultipart, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
import type { ReponseDocumentSigne } from './documentsGalerie.js';

export interface OptionLocation {
  prixConvenuCentimes: number;
  pourcentagePointsDeBase: number;
  plafondCentimes: number;
  valableJusquAu: string;
  factureLeveeId: string | null;
  leveeLe: string | null;
}
export interface LigneLocation {
  id: string;
  oeuvreId: string;
  exemplaireId: string | null;
  titre: string;
  loyerMensuelHtCentimes: number;
  valeurReferenceCentimes: number;
  debutLe: string;
  finLe: string | null;
  retourConstateLe: string | null;
  ignoreeAuRetour: boolean;
  motifFin: 'rotation' | 'vente' | 'fin' | null;
  optionAchat: OptionLocation | null;
}
export interface MouvementDepotLocation {
  type: 'encaissement' | 'restitution' | 'retenue';
  montantCentimes: number;
  date: string;
  mode: string;
  reference: string;
  motif: string;
}
export interface ContratLocationPresente {
  id: string;
  numero: string;
  version: number;
  etat: EtatContratLocation;
  etatEffectif: EtatContratLocation;
  locataireId: string;
  adresseInstallation: Adresse;
  debutLe: string;
  finLe: string | null;
  dureeMois: number | null;
  preavisMois: number;
  jourFacturation: number;
  lignes: LigneLocation[];
  assurance: ContratLocationCreation['assurance'];
  conditionsRotation: ContratLocationCreation['conditionsRotation'];
  notes: string;
  optionAchat: OptionLocation | null;
  depotGarantie: { montantCentimes: number; mouvements: MouvementDepotLocation[] };
  contratSigne: { nom: string; deposeLe: string } | null;
  documentContrat: string | null;
}
export interface EcheanceLocation {
  id?: string;
  contratId: string;
  numero?: string;
  debutPeriode: string;
  finPeriode: string;
  montantHtCentimes: number;
  facture?: { id: string; numero: string | null; etat: string } | null;
}
export interface VueLocations {
  mrrHtCentimes: number;
  serieMrr: { mois: string; montantHtCentimes: number }[];
  oeuvresLouees: { contratId: string; ligneId: string; oeuvreId: string; titre: string }[];
  echeancesAVenir: {
    contratId: string;
    numero: string;
    debutPeriode: string;
    finPeriode: string;
    dateFacturation: string;
    enRetard: boolean;
  }[];
  optionsOuvertes: { contratId: string; ligneId: string | null; option: OptionLocation }[];
}
export type OngletEcheances = 'a_facturer' | 'emises' | 'impayees' | 'a_rattraper';
export interface FiltresLocations {
  etat?: EtatContratLocation;
  locataireId?: string;
  oeuvreId?: string;
  page?: number;
}
export interface ResultatGesteLocation {
  contrat?: ContratLocationPresente;
  facture?: { id: string; lignes: { prixUnitaireCentimes: number; quantite: number }[] } | null;
  prixNetCentimes?: number;
  document?: ReponseDocumentSigne;
}
const racine = (g: string) => `/g/${encodeURIComponent(g)}/locations`;
const fiche = (g: string, id: string) => `${racine(g)}/${encodeURIComponent(id)}`;
export const locationsApi = {
  lister: (g: string, filtres: FiltresLocations = {}, appel?: AppelApi) => {
    const query = new URLSearchParams();
    for (const [cle, valeur] of Object.entries(filtres))
      if (valeur !== undefined) query.set(cle, String(valeur));
    return api.get<{ contrats: ContratLocationPresente[]; pagination: Pagination }>(
      `${racine(g)}?${query}`,
      { galerie: g, ...appel },
    );
  },
  obtenir: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ contrat: ContratLocationPresente; echeances: EcheanceLocation[] }>(fiche(g, id), {
      galerie: g,
      ...appel,
    }),
  vue: (g: string, appel?: AppelApi) =>
    api.get<VueLocations>(`${racine(g)}/vue-ensemble`, { galerie: g, ...appel }),
  echeances: (g: string, appel?: AppelApi) =>
    api.get<Record<OngletEcheances, EcheanceLocation[]>>(`${racine(g)}/echeances`, {
      galerie: g,
      ...appel,
    }),
  reglages: (g: string, appel?: AppelApi) =>
    api.get<{ reglages: { emissionAutomatique: boolean } }>(`${racine(g)}/reglages`, {
      galerie: g,
      ...appel,
    }),
  historique: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ contrats: ContratLocationPresente[] }>(
      `/g/${encodeURIComponent(g)}/oeuvres/${encodeURIComponent(id)}/locations`,
      { galerie: g, ...appel },
    ),
  creer: (g: string, corps: ContratLocationCreation, appel?: AppelApi) =>
    api.post<{ contrat: ContratLocationPresente }>(racine(g), corps, { galerie: g, ...appel }),
  modifier: (g: string, id: string, corps: Record<string, unknown>, appel?: AppelApi) =>
    api.patch<ResultatGesteLocation>(fiche(g, id), corps, { galerie: g, ...appel }),
  geste: (g: string, id: string, geste: string, corps: Record<string, unknown>, appel?: AppelApi) =>
    api.post<ResultatGesteLocation>(`${fiche(g, id)}/${geste}`, corps, { galerie: g, ...appel }),
  regler: (g: string, emissionAutomatique: boolean, appel?: AppelApi) =>
    api.put<{ reglages: { emissionAutomatique: boolean } }>(
      `${racine(g)}/reglages`,
      { emissionAutomatique },
      { galerie: g, ...appel },
    ),
  deposer: (g: string, id: string, version: number, fichier: File, appel?: AppelApi) => {
    const corps = new FormData();
    corps.append('fichier', fichier);
    corps.append('version', String(version));
    return requeteMultipart<ResultatGesteLocation>(`${fiche(g, id)}/contrat-signe`, corps, {
      galerie: g,
      ...appel,
    });
  },
  signe: (g: string, id: string, appel?: AppelApi) =>
    api.get<{ document: ReponseDocumentSigne }>(`${fiche(g, id)}/contrat-signe`, {
      galerie: g,
      ...appel,
    }),
};
export function useLocations(g: string, filtres: FiltresLocations = {}) {
  return useQuery({
    queryKey: cleMetier(g, 'locations', 'liste', filtres),
    queryFn: (c) => locationsApi.lister(g, filtres, appelDeRequete(c)),
  });
}
export function useLocation(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'locations', 'fiche', id),
    queryFn: (c) => locationsApi.obtenir(g, id, appelDeRequete(c)),
  });
}
export function useVueLocations(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'locations', 'vue'),
    queryFn: (c) => locationsApi.vue(g, appelDeRequete(c)),
  });
}
export function useEcheancesLocations(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'locations', 'echeances'),
    queryFn: (c) => locationsApi.echeances(g, appelDeRequete(c)),
  });
}
export function useReglagesLocations(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'locations', 'reglages'),
    queryFn: (c) => locationsApi.reglages(g, appelDeRequete(c)),
  });
}
export function useHistoriqueLocations(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'locations', 'historique', id),
    queryFn: (c) => locationsApi.historique(g, id, appelDeRequete(c)),
  });
}
export function useMutationLocation<T, R>(
  g: string,
  fn: (valeur: T, appel: AppelApi) => Promise<R>,
) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: fn,
      onSuccess: () => {
        for (const collection of ['locations', 'oeuvres', 'ventes', 'contacts'])
          void client.invalidateQueries({ queryKey: cleMetier(g, collection) });
      },
    }),
  );
}
